// src/admin/pages/AdminViewBooking.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { get, post } from "../../../lib/http";
import { API } from "../../../config/api";
import { toast } from "react-toastify";
import { formatDateTimeIST, formatEventDateTime } from "../../../utils/datetime.js";

const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function Row({ label, children }) {
    return (
        <div className="flex items-start gap-2 text-sm">
            <span className="min-w-[92px] text-slate-600">{label}</span>
            <span className="text-slate-900 break-words">{children || "-"}</span>
        </div>
    );
}

function Badge({ tone = "slate", children }) {
    const tones = {
        slate: "bg-slate-50 text-slate-700 ring-slate-200",
        green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        amber: "bg-amber-50 text-amber-700 ring-amber-200",
        red: "bg-rose-50 text-rose-700 ring-rose-200",
        blue: "bg-blue-50 text-blue-700 ring-blue-200",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ring-1 ${tones[tone]}`}>
            {children}
        </span>
    );
}

export default function AdminViewBooking() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [booking, setBooking] = useState(null);
    const [tickets, setTickets] = useState(null);
    const [payment, setPayment] = useState(null);
    const [error, setError] = useState("");

    // unify “assignee” snapshot across any source
    const assignee = useMemo(() => {
        return booking?.assignedBy || payment?.issuedBy || tickets?.issuedBy || null;
    }, [booking, payment, tickets]);

    useEffect(() => {
        let on = true;
        (async () => {
            setLoading(true);
            setError("");

            try {
                const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
                const headers = { Authorization: token ? `Bearer ${token}` : undefined };

                // Prefer full endpoint if available
                try {
                    const full = await get(`${API.booking.getAdminBookingById}/${id}/full`, { headers });
                    if (full?.booking || full?.tickets || full?.payment) {
                        if (!on) return;
                        setBooking(full.booking || null);
                        setTickets(full.tickets || null);
                        setPayment(full.payment || null);
                        return;
                    }
                } catch {
                    /* fallback below */
                }

                // Fallback: three calls
                const [bRes, tRes, pRes] = await Promise.all([
                    get(`${API.booking.getAdminBookingById}/${id}`, { headers }),
                    get(`${API.booking.getAdminTicketsByBooking}/${id}`, { headers }),
                    get(`${API.booking.getAdminPaymentByBooking}/${id}`, { headers }),
                ]);

                if (!on) return;
                setBooking(bRes?.booking || bRes || null);
                setTickets(tRes?.tickets || tRes || null);
                setPayment(pRes?.payment || pRes || null);

                if (!bRes?.booking && !bRes?._id) {
                    toast.warn("Booking not found.");
                }
            } catch (e) {
                if (!on) return;
                const msg = e?.message || "Failed to load booking";
                setError(msg);
                toast.error(msg);
            } finally {
                if (on) setLoading(false);
            }
        })();
        return () => {
            on = false;
        };
    }, [id]);

    const totalTickets = useMemo(() => {
        return (tickets?.ticketCategories || []).reduce(
            (s, c) => s + Number(c.finalQuantity || 0),
            0
        );
    }, [tickets]);

    const getFilenameFromDisposition = (cd = "") => {
        // e.g. attachment; filename="ticket_ABC123.pdf"
        const m = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(cd);
        if (m && m[1]) return decodeURIComponent(m[1]);
        return `ticket_${Date.now()}.pdf`;
    };

    const onDownloadPDF = async () => {
        try {
            const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

            const resp = await fetch(`${API.booking.getAdminBookingPDF}/${id}`, {
                method: "GET",
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}), // header से auth, URL साफ़
                    Accept: "application/pdf",
                },
            });

            console.log({ resp });
            // JSON error handle
            const ct = resp.headers.get("content-type") || "";
            if (!resp.ok) {
                if (ct.includes("application/json")) {
                    const errJson = await resp.json().catch(() => ({}));
                    throw new Error(errJson?.message || "Failed to generate PDF");
                }
                throw new Error("Failed to generate PDF");
            }

            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);

            // filename header से लें (backend नीचे expose करेगा)
            const cd = resp.headers.get("content-disposition") || "";
            const filename = getFilenameFromDisposition(cd);

            const a = document.createElement("a");
            a.href = url;
            a.download = filename;                  // ⟵ direct download
            document.body.appendChild(a);
            a.click();
            a.remove();

            // cleanup
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error(e?.message || "Failed to download PDF");
        }
    };

    const onResendEmail = async () => {
        try {
            const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
            await post(
                `${API.booking.postAdminBookingResendEmail}/${id}`,
                {},
                { headers: { Authorization: token ? `Bearer ${token}` : undefined } }
            );
            toast.success("Email queued to buyer.");
        } catch (e) {
            toast.error(e?.message || "Failed to resend email");
        }
    };

    if (loading) return <div className="p-6 text-sm text-slate-600">Loading…</div>;
    if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
    if (!booking) return <div className="p-6 text-sm text-slate-600">Not found.</div>;

    const eventName = booking?.eventId?.name || "-";
    const eventDate = booking?.eventId?.date ? formatEventDateTime(booking.eventId.date) : "-";
    const venue = booking?.eventId?.venue?.name || "-";

    const buyer = booking?.userId || payment?.payer || tickets?.buyer || {};
    const payMethod = payment?.paymentMethod || booking?.paymentMode || "-";
    const payStatus = payment?.paymentStatus || booking?.paymentStatus || "-";
    const amount = payment?.finalAmount ?? booking?.finalAmount ?? 0;
    const discountValue = payment?.discountValue ?? booking?.discountValue ?? 0;

    return (
        <div className="mx-auto max-w-5xl p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Booking Details</h1>
                    <div className="mt-1 text-sm text-slate-500">
                        {booking?.bookingCode ? <>Code: <b>{booking.bookingCode}</b> • </> : null}
                        #{booking?._id}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <Badge
                            tone={
                                payStatus === "Paid"
                                    ? "green"
                                    : payStatus === "Pending"
                                        ? "amber"
                                        : payStatus === "Failed"
                                            ? "red"
                                            : "slate"
                            }
                        >
                            {payStatus}
                        </Badge>
                        <Badge>{payMethod}</Badge>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => navigate("/admin/bookings")} className="rounded-xl border px-4 py-2 text-sm">
                        All Bookings
                    </button>
                    <button onClick={() => navigate(-1)} className="rounded-xl border px-4 py-2 text-sm">
                        Back
                    </button>
                    <button onClick={onDownloadPDF} className="rounded-xl border px-4 py-2 text-sm">
                        Download PDF
                    </button>
                    {buyer?.email && (
                        <button
                            onClick={onResendEmail}
                            className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm"
                        >
                            Resend Email
                        </button>
                    )}
                </div>
            </div>

            {/* Event + Buyer */}
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border bg-white p-5">
                    <h3 className="mb-3 text-base font-semibold text-slate-900">Event</h3>
                    <div className="space-y-1">
                        <Row label="Name:"><b>{eventName}</b></Row>
                        <Row label="Date:">{eventDate}</Row>
                        <Row label="Venue:">{venue}</Row>
                    </div>
                </div>

                <div className="rounded-2xl border bg-white p-5">
                    <h3 className="mb-3 text-base font-semibold text-slate-900">Buyer</h3>
                    <div className="space-y-1">
                        <Row label="Name:">{buyer?.name}</Row>
                        <Row label="Email:">{buyer?.email}</Row>
                        <Row label="Mobile:">{buyer?.mobile}</Row>
                    </div>
                </div>
            </div>

            {/* Issued By (Assignee) */}
            {assignee ? (
                <div className="rounded-2xl border bg-white p-5">
                    <h3 className="mb-3 text-base font-semibold text-slate-900">Issued By</h3>
                    <div className="grid gap-1 sm:grid-cols-3 text-sm">
                        <Row label="Name:">{assignee?.name}</Row>
                        <Row label="Email:">{assignee?.email}</Row>
                        <Row label="Mobile:">{assignee?.mobile}</Row>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                        (Snapshot from <code>booking.assignedBy</code> or <code>payment.issuedBy</code> / <code>tickets.issuedBy</code>.)
                    </p>
                </div>
            ) : null}

            {/* Payment */}
            <div className="rounded-2xl border bg-white p-5">
                <h3 className="mb-3 text-base font-semibold text-slate-900">Payment</h3>
                <div className="grid gap-2 sm:grid-cols-3 text-sm">
                    <Row label="Method:"><b>{payMethod}</b></Row>
                    <Row label="Status:"><b>{payStatus}</b></Row>
                    <Row label="Amount:"><b>{fmtInr(amount)}</b></Row>
                    <Row label="Transaction:">{payment?.transactionId}</Row>
                    <Row label="Discount:">{fmtInr(discountValue)}</Row>
                    <Row label="Created:">
                        {payment?.createdAt
                            ? formatDateTimeIST(payment.createdAt)
                            : booking?.createdAt
                                ? formatDateTimeIST(booking.createdAt)
                                : "-"}
                    </Row>
                </div>
            </div>

            {/* Tickets */}
            <div className="rounded-2xl border bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-slate-900">
                        Tickets ({totalTickets})
                    </h3>
                </div>

                <div className="space-y-4">
                    {(tickets?.ticketCategories || []).map((c) => (
                        <div key={c.categoryId} className="rounded-xl border p-4">
                            <div className="flex items-center justify-between">
                                <div className="font-semibold text-slate-900">{c.categoryId}</div>
                                <div className="text-sm text-slate-600">
                                    qty: <b>{c.finalQuantity}</b> (paid {c.paidQuantity} + free {c.freeQuantity})
                                </div>
                            </div>
                            {!!c.codes?.length && (
                                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                    {c.codes.map((code, idx) => (
                                        <div key={idx} className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-mono">{code}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {tickets?.qrCode && (
                    <div className="mt-4">
                        <div className="text-xs text-slate-500 mb-1">QR (booking-level)</div>
                        <img alt="QR" src={tickets.qrCode} className="h-32 w-32 object-contain" />
                    </div>
                )}
            </div>
        </div>
    );
}
